import {
    Component,
    Input,
    OnInit,
    HostListener,
    ViewChild,
    ElementRef,
    AfterViewInit,
    ViewEncapsulation,
    NgZone,
    ChangeDetectorRef
} from '@angular/core';
import { ViewportScroller } from "@angular/common";
import {ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterEvent, Event} from "@angular/router";
import { ShareDataService, CarouselService, EventBusService } from "@signature-it/ngx-generic/services";
import { RestUrlsConfig, EntityConfig } from "@signature-it/ngx-generic/config";
import { EventName } from '@signature-it/ngx-generic/classes';
import {filter} from "rxjs/operators";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

let tocInstanceNumber = 0;

@Component({
    selector: 'app-table-of-contents',
    templateUrl: './table-of-contents.component.html',
    styleUrls: ['./table-of-contents.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TableOfContentsComponent implements OnInit, AfterViewInit	{
    cmpID = tocInstanceNumber++;
    anchorsList = [];
    href: string = "";
    currentTarget: string;
    targetsParent;
    firstActive: boolean = true;
    fragment: string;
    dropDownOpen = false;
    imgCollection =  {};
    entityConfig;
    currentUrlPath: string = '';
    @Input() selectedItemText;
    @Input() targetSelector = 'h2';
    @Input() mobileHS;
    @Input() api: string;
    @Input() entityVar: string;
    @Input() entityTypeVar: string;
    @Input() fieldCollectionVar: string;
    @Input() key: string;
    @Input() displayMode: string = 'flat';
    @Input() externalLinkSameTab: boolean = false;
    @Input() trackerCalibration: number = 0;
    @ViewChild ('scrollerEl') scrollerEl: ElementRef;
    degel = true;

    constructor(private scroller: ViewportScroller,
                private router: Router,
                private route: ActivatedRoute,
                private host: ElementRef,
                public shareSvc: ShareDataService,
                protected carouselService: CarouselService,
                protected restUrlsConf: RestUrlsConfig,
                private eventBusService: EventBusService,
                private ngZone: NgZone,
                private cd: ChangeDetectorRef) {

        router.events.pipe(
            filter((e: Event | RouterEvent): e is RouterEvent => e instanceof NavigationStart)
        ).pipe(takeUntilDestroyed()).subscribe((e: RouterEvent) => {
            let urlPath = e.url.split('#')[0];
            if(urlPath !== this.currentUrlPath){ // if it's not a toc navigation -> then rebuild tree
                this.currentUrlPath = urlPath;
                this.ngZone.runOutsideAngular(() => {
                    setTimeout(() =>  this.buildList(), 250) ; //setTimeOut for when the component is loaded before other entities in the same page
                });
            }
        });
    }

    async ngOnInit() {
        await this.init();
        this.eventBusService.on(EventName.HIDE_CMP_DEPEND_EL, async () => {
            this.anchorsList = [];
            await this.init();
        });
        this.eventBusService.on(EventName.ENTITY_NEW_HTML_RENDERED, async () => {
            this.anchorsList = [];
            await this.init();
        });
    }

    ngAfterViewInit(): void {
        // for when the url already contains a fragment on page load
        this.route.fragment.subscribe(fragment => {
            this.scrollToTarget(fragment);
        });

        // for when fragment changes, either by using the <a> routing elements of the component, or by using the back/forward buttons of the browser, or when navigating to the current page form somewhere else
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                let fragmentIdx = event.urlAfterRedirects.lastIndexOf('#');
                if (fragmentIdx >= 0 && fragmentIdx < event.urlAfterRedirects.length - 1) {
                    let fragment = event.urlAfterRedirects.substring(fragmentIdx+1);
                    this.scrollToTarget(fragment);
                }
            }
        });
    }


    async init(){

        if(this.api && this.entityVar && this.entityTypeVar && this.fieldCollectionVar){
            this.api += `?entityVar=${this.entityVar}&entityTypeVar=${this.entityTypeVar}&collectionVar=${this.fieldCollectionVar}`;

            let galleryItems = await this.carouselService.getGalleryFromApi(this.api).toPromise();

            galleryItems.forEach((item) => {
                let itemKey = item['drawing_key'];
                this.imgCollection[itemKey] = item['src'];
            });

        }


        this.href = this.router.url;

        // this.buildList();
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() =>  {
                    this.buildList();
                    this.cd.detectChanges() // update view with the new list
                },
                250) ; //setTimeOut for when the component is loaded before other entities in the same page
        });

        const entityConfig = EntityConfig.ngxTocConfig[this.key];
        this.entityConfig = entityConfig;

        if(entityConfig?.responsive && Object.keys(entityConfig?.responsive).length){
            this.displayMode = this.getResponsiveConfigProperty(entityConfig?.responsive, 'displayMode') || this.displayMode;
        }

        this.targetsParent = this.anchorsList.length > 0 ? this.getTargetsCommonParent(): null;
    }

    buildList(){
        let newAnchorsList = [];
        let anchors = Array.from(document.querySelectorAll<HTMLElement>(this.targetSelector));
        let idCounter = 0;
        anchors.forEach(value => {
            // add id attribute to all targets, if absent
            if(value.id == ""){
                value.id = 'toc-'+this.cmpID+'-target-'+idCounter;
            }
            idCounter++;
            // set the menu-items list object
            if (value.offsetParent && value.offsetWidth !== 0 && value.offsetHeight !== 0 || value.getAttribute('data-toc-outer-link')){ // avoid adding DOM hidden targets to list
                newAnchorsList.push({
                    'targetId': value.id,
                    'name': value.getAttribute('data-toc-name') || value.innerText ,
                    'className': value.getAttribute('data-toc-class'),
                    'imageSrc': this.imgCollection[value.getAttribute('data-toc-drawing-key')] || null,
                    'outerLink': value.getAttribute('data-toc-outer-link') || null,
                    'active': false             
   });
            }
        });
        this.anchorsList = newAnchorsList;
    }

    getResponsiveConfigProperty(responsiveEntityConfig = {}, property = ''){
        let responsiveKeys = Object.keys(responsiveEntityConfig);
        const currentKey = this.shareSvc.getCurrentBp(responsiveKeys);
        return responsiveEntityConfig[currentKey][property];
    }



    onPointerClick(fragment: string, event: MouseEvent) {
        // Prevent default link behavior
        event.preventDefault();
        // Set the 'active' property for the clicked item
        this.setActiveItem(fragment);
        // Scroll to the clicked anchor
        this.scrollToTarget(fragment); 
        this.selectedItemText = this.anchorsList.find(link => link.targetId === fragment)?.name || '';
    }

    disableScrollListener() {
        window.removeEventListener('scroll', this.onScroll);
    }

    enableScrollListener() {
        window.addEventListener('scroll', this.onScroll);
    }

    setActiveItem(fragment: string) {
    
    // Set 'active' property for the clicked item
        this.anchorsList.forEach(link => {
            link.active = link.targetId === fragment;
        });
    }
    scrollToTarget(anchor){
         if(<HTMLElement>document.getElementById(anchor)) {
            this.degel =false;                
            document.getElementById(anchor).scrollIntoView();
        }

    }

    getTargetsCommonParent(){
        let range = document.createRange();
        const nodes = [];
        this.anchorsList.forEach(item => {
            nodes.push(document.getElementById(item.targetId));
        });
        nodes.forEach(range.selectNode, range);
        range.setStart(nodes[0], 0);
        range.setEnd(nodes[nodes.length - 1], 0);
        return range.commonAncestorContainer;
    }


 
    @HostListener('window:scroll', ['$event'])
        onScroll(event: any) {
            if(!this.degel) {
               this.degel = true;
               return;
            }
  
        let currentTarget: string;
        let flag = false;

        for (let i = 0; i < this.anchorsList.length; i++) {
            let currentItemId: string = this.anchorsList[i].targetId;
            let currentItemElTop = document.getElementById(currentItemId)?.offsetTop;

            if (window.pageYOffset >= (currentItemElTop - this.trackerCalibration) && 
                !document.getElementById(currentItemId).getAttribute('data-toc-outer-link')) {
                flag = false;
                currentTarget = currentItemId;
            } else if (!flag) {
                currentTarget = currentItemId;
                flag = true;
            }

            if (this.currentTarget !== currentTarget) {
                this.currentTarget = currentTarget;
                // Set 'active' property for the corresponding item
                this.setActiveItem(currentTarget);
            }

            if (i == this.anchorsList.length - 1 && this.targetsParent && 
                window.pageYOffset > this.targetsParent.offsetTop + this.targetsParent.offsetHeight) {
                this.currentTarget = '';
            }

            if (i == this.anchorsList.length - 1 && this.mobileHS && 
                this.scrollerEl.nativeElement.querySelector('.active')) {
                let pointerElActive = this.scrollerEl.nativeElement.querySelector('.active');
                let activeElWidth = pointerElActive.offsetWidth;
                let activeElRightPos = pointerElActive.getBoundingClientRect().right;
                let scrollerRightPos = this.scrollerEl.nativeElement.getBoundingClientRect().right;
                this.scrollerEl.nativeElement.scrollLeft += (activeElRightPos - scrollerRightPos + activeElWidth / 2);
            }
        }
    } 
}
